import { CalendarToday } from "@mui/icons-material"
import intl from "src/internationalization/intl"
import {
    BoxCard,
    Button,
    CircularProgress,
    CommitmentAccordion,
    Grid,
    InnerSection,
    LinearProgress,
    RankingCard,
    Section,
    Stack,
    TabFilters,
    Typography
} from "src/lib"
import { AdvisorRankingsCollapsible } from "./AdvisorRankingsCollapsible"
import { useCommitmentsSection } from "./useCommitmentsSection"
import { useRankingAssets } from "./useRankingAssets"
import type { ICommitmentsSectionProps } from "./types"

const CommitmentsSection = ({
    hideRanking = false,
    rankingOnly = false,
    rankingsReadOnly = false,
}: ICommitmentsSectionProps = {}) => {
    const {
        loading,
        error,
        commitmentsList,
        cashCommitmentsList,
        finalCommitmentsList,
        isOpen,
        toggleAccordion,
        handleClickAIButton,
        expandAll,
        collapseAll,
        accordions,
        totalEvents,
        totalOpportunities,
        unresolvedLiquidityCount,
        uniqueClientsCount,
        resolvedLiquidityEventsCount,
        resolvedLiquidityEventsPercentage,
        clientsWithOpportunitiesCount,
        resolvedClientsWithOpportunitiesCount,
        resolvedClientsWithOpportunitiesPercentage,
        clientOpportunities,
        filteredClientOpportunities,
        uniqueCashToInvestClientsCount,
        liquidityFilters,
        selectedLiquidityFilter,
        handleLiquidityFilterChange,
        liquidityStreamFilters,
        selectedLiquidityStreamFilter,
        handleLiquidityStreamFilterChange,
        eventValueFilters,
        selectedEventValueFilter,
        handleEventValueFilterChange,
        periodFilters,
        selectedPeriodFilter,
        handlePeriodFilterChange,
        eventsAccordions,
        isEventOpen,
        toggleEventAccordion,
        expandAllEvents,
        collapseAllEvents,
    } = useCommitmentsSection()

    const { rankingAssets } = useRankingAssets()

    const toDateKey = (value?: string) => {
        if (!value) return ""
        const base = String(value).split("T")[0]
        const parts = base.split("-")
        if (parts.length !== 3) return base
        const [year, month, day] = parts
        return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`
    }
    const todayKey = toDateKey(new Date().toISOString())

    if (rankingOnly) {
        return (
            <Section
                title={intl("rankingSectionTitle")}
                icon={<CalendarToday />}
                onClickAIButton={handleClickAIButton}
            >
                {loading ? (
                    <Grid container justifyContent="center" sx={{ mt: 4, minHeight: "400px" }} alignItems="center">
                        <CircularProgress color="primary" />
                    </Grid>
                ) : error ? (
                    <Typography sx={{ mt: 2, color: (theme) => (theme as any).palette?.accentError?.main }}>
                        {error}
                    </Typography>
                ) : (
                    <Grid container gap={2} justifyContent="flex-start" sx={{ mt: 2 }}>
                        {rankingAssets.map((item, index) => (
                            <Grid item key={item.assetKey ?? index}>
                                <RankingCard
                                    assetKey={item.assetKey}
                                    asset={item.asset}
                                    date={item.date}
                                    description={item.description}
                                    rankings={item.rankings}
                                    locked={item.locked}
                                />
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Section>
        )
    }

    return (
        <Section
            title={intl("commitments")}
            icon={<CalendarToday />}
            onClickAIButton={handleClickAIButton}
            headerComponents={
                <>
                    <TabFilters
                        filters={liquidityStreamFilters}
                        selectedFilter={selectedLiquidityStreamFilter}
                        onFilterChange={handleLiquidityStreamFilterChange}
                    />
                    <TabFilters
                        filters={eventValueFilters}
                        selectedFilter={selectedEventValueFilter}
                        onFilterChange={handleEventValueFilterChange}
                    />
                    <TabFilters
                        filters={liquidityFilters}
                        selectedFilter={selectedLiquidityFilter}
                        onFilterChange={handleLiquidityFilterChange}
                    />
                    <TabFilters
                        filters={periodFilters}
                        selectedFilter={selectedPeriodFilter}
                        onFilterChange={handlePeriodFilterChange}
                    />
                </>
            }
        >
            {loading ? (
                <Grid container justifyContent="center" sx={{ mt: 4, minHeight: '400px' }} alignItems="center">
                    <CircularProgress color="primary" />
                </Grid>
            ) : error ? (
                <Typography sx={{ mt: 2, color: (theme) => (theme as any).palette?.accentError?.main }}>
                    {error}
                </Typography>
            ) : (
                <>
                    {!hideRanking && rankingsReadOnly ? (
                        <AdvisorRankingsCollapsible />
                    ) : null}
                    {!hideRanking && !rankingsReadOnly ? (
                        <InnerSection title={intl("rankingSectionTitle")}>
                            <Grid container gap={2} justifyContent="flex-start" sx={{ mt: 2 }}>
                                {rankingAssets.map((item, index) => (
                                    <Grid item key={item.assetKey ?? index}>
                                        <RankingCard
                                            assetKey={item.assetKey}
                                            asset={item.asset}
                                            date={item.date}
                                            description={item.description}
                                            rankings={item.rankings}
                                            locked={item.locked}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        </InnerSection>
                    ) : null}
                    <InnerSection title={'Lista de compromissos'} mt={2}>
                        <BoxCard sx={{ mt: 2 }}>
                            <InnerSection title={'Compromissos'}>
                                <Grid container sx={{ width: '100%', mt: 1 }} gap={1} justifyContent={'space-between'}>
                                    <Grid item xs={7} sx={{ width: '100%' }}>
                                        <LinearProgress
                                            value={resolvedLiquidityEventsPercentage ?? 0}
                                            variant="determinate"
                                        />
                                        <Typography variant="caption" sx={{ mt: 0.5, display: 'block' }}>
                                            {resolvedLiquidityEventsCount} de {totalEvents} feitos
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Grid container gap={2}>
                                            <Button variant="outlined" color="neutral" onClick={collapseAllEvents}>
                                                <Typography variant="body2">{intl('hideAll')}</Typography>
                                            </Button>
                                            <Button variant="outlined" color="neutral" onClick={expandAllEvents}>
                                                <Typography variant="body2">{intl('showAll')}</Typography>
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </InnerSection>
                        </BoxCard>
                    </InnerSection>

                </>
            )}

            {!loading && !error && (
                <Stack spacing={3} sx={{ marginTop: "70px", padding: "0px 20px" }}>
                    {finalCommitmentsList.map((commitment, idx: number) => {
                        const eventAccordionId = commitment.due_date + 1;
                        const eventAccordionIsOpen = isEventOpen(eventAccordionId);
                        const isToday = toDateKey(commitment.due_date) === todayKey
                        return (
                            <CommitmentAccordion.Root
                                key={commitment.due_date}
                                commitmentId={eventAccordionId}
                                sx={{ mt: idx === 0 ? 0 : 3 }}
                            >
                                <CommitmentAccordion.Summary
                                    commitment={commitment}
                                    opportunitiesClientsCount={isToday ? filteredClientOpportunities.length : 0}
                                    open={eventAccordionIsOpen}
                                    onToggle={() => toggleEventAccordion(eventAccordionId)}
                                />
                                <CommitmentAccordion.Details
                                    commitment={commitment}
                                    open={eventAccordionIsOpen}
                                    isOpen={isOpen}
                                    toggleAccordion={toggleAccordion}
                                    clientOpportunities={isToday ? filteredClientOpportunities : []}
                                />
                            </CommitmentAccordion.Root>
                        )
                    })}
                </Stack>
            )}
        </Section>
    )
}

export { CommitmentsSection }

